import ApiService from "./api.service";
import ApiError from "../exceptions/api.error";
import { AxiosResponse } from "axios";
import { AppointmentDTO } from "@/models/AppointmentDTO";
import { EventDTO } from "@/models/EventDTO";
import { CenterAvailableDTO } from "@/models/CenterDTO";
import { PublicAppointmentParticipantRequest } from "@/models/PublicAppointmentParticipantRequest";
import { PublicAppointmentParticipantResponse } from "@/models/PublicAppointmentParticipantResponse";
import { DateAvailabilityResponse } from "@/models/DateAvailabilityResponse";

export default class AppointmentService {
  public static readonly BaseUrl = `/rdv/Appointment`;
  /**
   * Récuppère les occurence pour un centre
   * @date 20.09.2023 - 10:53:01
   *
   * @public
   * @static
   * @async
   * @param {number} centerId
   * @param {number} partId
   * @param {string} date
   * @param {boolean} [rdvLong=false]
   * @returns {Promise<EventDTO[]>}
   */
  public static async GetSlotsByCenterId(
    centerId: number,
    partId: number,
    date: string,
    rdvLong: boolean = false
  ): Promise<EventDTO[]> {
    return await ApiService.get(
      `${this.BaseUrl}/get-slot-by-center-id/${centerId}/${partId}/${date}/${rdvLong}`
    )
      .then((value: AxiosResponse) => {
        return value.data.data as EventDTO[];
      })
      .catch(err => {
        throw new ApiError(err.response.status, err.response.data.message);
      });
  }

  public static async GetFirstSlot(
    rdvLong: boolean,
    partId: number
  ): Promise<CenterAvailableDTO[]> {
    return await ApiService.get(
      `${this.BaseUrl}/get-first-slots/${rdvLong}/${partId}`
    )
      .then((value: any) => {
        return value.data as CenterAvailableDTO[];
      })
      .catch(err => {
        throw new ApiError(err.response.status, err.response.data.message);
      });
  }

  // Creates an appointment
  public static async CreateAppointment(
    data: AppointmentDTO
  ): Promise<AppointmentDTO | null> {
    return await ApiService.post(`${this.BaseUrl}/create-appointment`, data)
      .then((value: AxiosResponse) => {
        return value.data === "" ? null : (value.data as AppointmentDTO);
      })
      .catch(err => {
        throw new ApiError(err.response.status, err.response.data.message);
      });
  }

  // get all appointments
  public static async GetAllAppointments(): Promise<AppointmentDTO[]> {
    return await ApiService.get(`${this.BaseUrl}/get-all-appointments`).then(
      (value: AxiosResponse) => {
        return value.data as AppointmentDTO[];
      }
    );
  }
  // get appointment by id
  public static async GetAppointmentById(
    appId: number
  ): Promise<AppointmentDTO> {
    return await ApiService.get(
      `${this.BaseUrl}/get-appointment-by-id/${appId}`
    ).then((value: AxiosResponse) => {
      return value.data as AppointmentDTO;
    });
  }
  // get appointment by part id
  public static async GetAppointmentByPartId(
    partId: number | undefined
  ): Promise<AppointmentDTO> {
    return await ApiService.get(`${this.BaseUrl}/${partId}/current`).then(
      (value: AxiosResponse) => {
        return value.data as AppointmentDTO;
      }
    );
  }
  // edit appointement
  public static async EditAppointment(data: AppointmentDTO): Promise<void> {
    await ApiService.put(`${this.BaseUrl}/edit-appointment`, data);
  }

  // delete appointment
  public static async DeleteAppointment(appId: number): Promise<void> {
    await ApiService.post(`${this.BaseUrl}/delete-appointment/${appId}`, {});
  }

  /**
   * Check if phone number is mandatory
   * @returns {boolean} true phone number mandatory
   */
  public static async IsPhoneNumberMandatory() {
    return await ApiService.get(
      `${this.BaseUrl}/is-phoneNumber-mandatory`
    ).then((response: AxiosResponse) => {
      return response.data as boolean;
    });
  }

  /**
   * Check if email address is mandatory
   * @returns {boolean} true email address mandatory
   */
  public static async IsEmailMandatory() {
    return await ApiService.get(`${this.BaseUrl}/is-email-mandatory`).then(
      (response: AxiosResponse) => {
        return response.data as boolean;
      }
    );
  }

  public static async GetMaxGeneratedSlotDate() {
    return await ApiService.get(`${this.BaseUrl}/get-max-slot-date`).then(
      (response: AxiosResponse) => {
        return response.data as Date;
      }
    );
  }

  /**
   * Create an instance of PublicAppointmentParticipant
   *
   * @param data
   *
   * @returns {PublicAppointmentParticipantResponse}
   */
  public static async createPublicAppointmentParticipant(
    data: PublicAppointmentParticipantRequest
  ): Promise<PublicAppointmentParticipantResponse | null> {
    return await ApiService.post(
      `${this.BaseUrl}/create-public-appointment-participant`,
      data
    )
      .then((value: AxiosResponse) => {
        return value.data === ""
          ? null
          : (value.data as PublicAppointmentParticipantResponse);
      })
      .catch(err => {
        throw new ApiError(err.response.status, err.response.data.message);
      });
  }

  /**
   * Edit an instance of PublicAppointmentParticipant
   *
   * @param data
   */
  public static async editPublicAppointmentParticipant(
    data: PublicAppointmentParticipantRequest
  ): Promise<void> {
    await ApiService.put(
      `${this.BaseUrl}/update-public-appointment-participant`,
      data
    );
  }

  /**
   * Get a PublicAppointmentParticipantResponse instance
   *
   * @returns {PublicAppointmentParticipantResponse}
   */
  public static async getPublicAppointmentParticipant(
    suId: number,
    partId: number,
    appId: number
  ) {
    return await ApiService.get(
      `${this.BaseUrl}/${suId}/${partId}/${appId}/get-public-appointment-participant`
    ).then((response: any) => {
      return response.data as PublicAppointmentParticipantResponse;
    });
  }

  /**
   * Get availability summary for calendar color-coding
   * Returns availability status for each date in the range
   *
   * @param centerId Center ID
   * @param partId Participant ID
   * @param rdvLong True if long appointment required
   * @param startDate Start date (YYYY-MM-DD format)
   * @param endDate End date (YYYY-MM-DD format)
   * @returns {DateAvailabilityResponse[]} List of date availability summaries
   */
  public static async GetAvailabilitySummary(
    centerId: number,
    partId: number,
    rdvLong: boolean,
    startDate: string,
    endDate: string
  ): Promise<DateAvailabilityResponse[]> {
    return await ApiService.get(
      `${this.BaseUrl}/get-availability-summary/${centerId}/${partId}/${rdvLong}?startDate=${startDate}&endDate=${endDate}`
    )
      .then((value: AxiosResponse) => {
        return value.data as DateAvailabilityResponse[];
      })
      .catch(err => {
        throw new ApiError(err.response.status, err.response.data.message);
      });
  }
}
